import { useTolgee } from "@tolgee/react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DEPLOY_ENV, MXCALL_URL } from "../../../environment.js";
import { useUserData } from "../../auth/useUserData.js";
import { Page } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";

type NavigationMessage = {
  type: "navigate";
  replace?: boolean;
} & (
  | { path: string }
  | { entity: "project"; id: string }
  | { entity: "crmCompany"; id: string }
  | { entity: "crmPerson"; personId: string; companyId: string }
  | { entity: "quote"; projectId: string; quoteId: string }
  | { entity: "requirement"; projectId: string; requirementId: string }
);

const isNavigationMessage = (data: unknown): data is NavigationMessage => {
  if (
    typeof data !== "object" ||
    data === null ||
    !("type" in data) ||
    data.type !== "navigate"
  ) {
    return false;
  }

  // Check for simple path navigation
  if ("path" in data && typeof data.path === "string") {
    return true;
  }

  // Check for entity-based navigation
  if ("entity" in data && typeof data.entity === "string") {
    const entity = data.entity;
    const id = "id" in data ? data.id : undefined;

    if (entity === "project" && typeof id === "string") {
      return true;
    }
    if (entity === "crmCompany" && typeof id === "string") {
      return true;
    }
    if (
      entity === "crmPerson" &&
      "personId" in data &&
      typeof data.personId === "string" &&
      "companyId" in data &&
      typeof data.companyId === "string"
    ) {
      return true;
    }
    if (
      entity === "quote" &&
      "projectId" in data &&
      typeof data.projectId === "string" &&
      "quoteId" in data &&
      typeof data.quoteId === "string"
    ) {
      return true;
    }
    if (
      entity === "requirement" &&
      "projectId" in data &&
      typeof data.projectId === "string" &&
      "requirementId" in data &&
      typeof data.requirementId === "string"
    ) {
      return true;
    }
  }

  return false;
};

export const CallAssistant = () => {
  const locale = useTolgee(["language"]).getLanguage();
  const viewer = useUserData().currentUser!;
  const location = useLocation();
  const navigate = useNavigate();

  const url = React.useMemo(() => {
    const url = new URL(MXCALL_URL);
    const pathname = url.pathname
      .concat(location.pathname.replace("/callassistant", ""))
      .replace(/\/+/g, "/");
    url.pathname = pathname;

    const urlSearchParams = new URLSearchParams(location.search);
    urlSearchParams.set("contractor_id", viewer.organisation.id);
    urlSearchParams.set("user_id", viewer.id);
    urlSearchParams.set("lang", locale.split("-").at(0)?.toLowerCase() ?? "de");
    if (DEPLOY_ENV !== "local" && window.location.hostname !== "localhost") {
      urlSearchParams.set("msys_url", window.location.origin);
    }
    url.search = urlSearchParams.toString();

    return url;
  }, [
    locale,
    location.pathname,
    location.search,
    viewer.organisation.id,
    viewer.id,
  ]);

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== url.origin) {
        return;
      }

      // Type guard and handle navigation message
      if (isNavigationMessage(event.data)) {
        const { replace = false } = event.data;
        let path: string | undefined;

        // Handle different navigation message types
        if ("path" in event.data) {
          path = event.data.path;
        } else if ("entity" in event.data) {
          // Convert entity-based navigation to path
          switch (event.data.entity) {
            case "project":
              path = `/projects/${event.data.id}`;
              break;
            case "crmCompany":
              path = `/crm/organisations/${event.data.id}`;
              break;
            case "crmPerson":
              path = `/crm/organisations/${event.data.companyId}/users/${event.data.personId}`;
              break;
            case "quote":
              path = `/projects/${event.data.projectId}/quotes/${event.data.quoteId}`;
              break;
            case "requirement":
              path = `/projects/${event.data.projectId}/requirements/${event.data.requirementId}`;
              break;
          }
        }

        if (path) {
          navigate(path, { replace });
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [navigate, url.origin]);

  return (
    <Page>
      <PageContainer $fullHeight $noPadding>
        <iframe
          style={{
            width: "100%",
            height: "100%",
            border: 0,
            padding: 0,
            margin: 0,
          }}
          src={url.toString()}
        ></iframe>
      </PageContainer>
    </Page>
  );
};

/*
The child iframe (Call Assistant) should be able to send navigation messages like this:

// Simple navigation (sufficient in most cases)
window.parent.postMessage({
    type: "navigate",
    path: "/some/path"
  },
  "*" // or specify parent origin for better security
);

// Navigation with replace (no history entry)
window.parent.postMessage({
    type: "navigate",
    path: "/some/path",
    replace: true
  },
  "*"
);
 */
